import { LocalFind, LocalInsert } from '@weblite/persist'
import { CallType } from '@weblite/ts-action/media-proxy'
import { checkLastRejectionAndShowNotificationPermissionDialog } from '$setup/notification'
import axios from 'axios'
import { checkContactPermissionDialog } from '$logic/contacts/service'
import { getAuthToken, isLoggedIn, loginUserFromAndroid } from '$logic/auth'
import {
  sendEventToTwa,
  setTwaTokenViaHttp,
} from '$logic/twaCommunication/twaCommunication.api'
import {
  TWA_EVENTS,
  TwaEventTypeField,
} from '$logic/twaCommunication/twaCommunication.types'
import { isAndroidApp } from '$func/environmentInfo'
import {
  getWsUrl,
  setPort,
  setWsUrl,
} from '$logic/twaCommunication/twaCommiunication.state'
import { getRoom } from '$logic/room'
import { CallNotification, callService } from '$logic/call'
import {
  ContactPermission,
  SessionStorageKeys,
} from '$logic/client-device/types'
import {
  sessionStorageGetItem,
  sessionStorageSetItem,
} from '$func/session-storage'
import { getRoomImageAndName } from '$func/room-image-and-name'
import { clientSettingsStorage } from '$logic/client-device/storage'
import { handleLink } from '$logic/router/url'
import hostConf from '$config/frontend'
import { handleAndroidPong } from '$logic/twaCommunication/websocket'
import { sendFcmTokenToServer } from '$logic/push/service'
import { StartUpModalQueue } from '$func/startup-modal-handler'
import { StartupModalPriorities } from '$helper/constants/startup-modal-priorities'
import { handleNewShareTarget } from '$logic/shared-target'

const NOTIF_PERM = 'notif_perm'
const CONTACT_PERM = 'contact_perm'
const TWA_RUN_FILE_PORT = 'twa_run_file_port'

const params = new URLSearchParams(window.location.search)
console.log('window.location.search ', window.location.search)
const notifPermission =
  params.get(NOTIF_PERM) || sessionStorageGetItem(SessionStorageKeys.NOTIF_PERM)

const twaRunFilePort = params.get(TWA_RUN_FILE_PORT)

export const contactPermission =
  params.get(CONTACT_PERM) ||
  sessionStorageGetItem(SessionStorageKeys.CONTACT_PERM)

if (contactPermission)
  sessionStorageSetItem(SessionStorageKeys.CONTACT_PERM, contactPermission)

if (notifPermission)
  sessionStorageSetItem(SessionStorageKeys.NOTIF_PERM, notifPermission)

export const updateTwaPermissions = () => {
  if (!isAndroidApp) return
  if (!isLoggedIn()) {
    sendEventToTwa(TWA_EVENTS.LOGOUT)
    return
  }

  if (contactPermission && contactPermission !== 'allow')
    StartUpModalQueue.add({
      onOpen: checkContactPermissionDialog,
      priority: StartupModalPriorities.grantContactPermission,
      nextOneIsManual: true,
    })

  if (notifPermission && notifPermission !== 'allow')
    StartUpModalQueue.add({
      onOpen: checkLastRejectionAndShowNotificationPermissionDialog,
      priority: StartupModalPriorities.grantNotificationPermission,
      nextOneIsManual: true,
    })

  if (contactPermission === 'allow') sendSyncContactsEventToTwa()
}

const sendAuthTokenToTwa = async (token: string) =>
  getWsUrl()
    ? sendEventToTwa(TWA_EVENTS.AUTH_TOKEN, { token })
    : setTwaTokenViaHttp(token)

export const setTwaAuthToken = (token = getAuthToken()) => {
  if (!isAndroidApp) return
  sendAuthTokenToTwa(token).catch(() =>
    setTimeout(setTwaAuthToken, 2000, token),
  )
}

export const setTwaHttpServerPort = setPort

export const setTwaWsServerUrl = setWsUrl

async function showCallNotification(
  roomId: string,
  event: TwaEventTypeField,
  callType: CallType = 'call',
) {
  const room = await getRoom(roomId)
  if (!room) return

  const { name, image } = await getRoomImageAndName(room.type, roomId)
  sendEventToTwa(event, {
    roomId,
    callType,
    callName: name,
    roomAvatar: image,
  } as CallNotification)
}

export const twaCommunication = {
  showCallNotification,
}

export const sendSyncContactsEventToTwa = () => {
  if (clientSettingsStorage.get()?.autoSyncContacts === false) return
  sendEventToTwa(TWA_EVENTS.SYNC_CONTACT)
}

const handleFirebaseToken = (token: string) => {
  const oldToken = LocalFind('twaFcmRegistrationId')
  if (token !== oldToken) {
    sendFcmTokenToServer(token)
    LocalInsert('twaFcmRegistrationId', token)
  }
}

const handleAuth = (token: string) => {
  const currentToken = LocalFind('auth')?.token

  if (!currentToken && token) {
    loginUserFromAndroid(token)
  }
}

export const parseOldAndroidMessage = ({
  type,
  payload,
}: {
  type: any
  payload: any
}) => {
  switch (type) {
    case 'FirebaseToken':
      handleFirebaseToken(payload.token)
      break
    case 'HandleLink':
      handleLink(new URL(payload.link, hostConf.FRONT_HOST).href)
      break
    case 'HandleNotificationPermission':
      sessionStorageSetItem(SessionStorageKeys.NOTIF_PERM, payload.notif_perm)
      break
    case 'HandleContactPermission':
      if (payload.contact_perm === 'allow')
        sendEventToTwa(TWA_EVENTS.SYNC_CONTACT)
      sessionStorageSetItem(
        SessionStorageKeys.CONTACT_PERM,
        payload.contact_perm,
      )
      break
    case 'AuthInfo':
      if (payload.token) handleAuth(payload.token)
      break
    case 'Pong':
      handleAndroidPong()
      break
    case 'acceptIncomingCall':
      callService.acceptIncomingCallByRoomId(payload.roomId)
      break
    case 'rejectIncomingCall':
      callService.rejectIncomingCallByRoomId(payload.roomId)
      break
    case 'endOnGoingCall':
      callService.endOngoingCallByRoomId(payload.roomId)
      break
    default:
      break
  }
}

export const parseAndroidMessage = ({
  type,
  payload,
}: {
  type: string
  payload: any
}) => {
  switch (type) {
    case 'FirebaseToken':
      handleFirebaseToken(payload.token)
      break
    case 'HandleLink':
      handleLink(new URL(payload.link, hostConf.FRONT_HOST).href)
      break
    case 'AuthInfo':
      if (payload.token) handleAuth(payload.token)
      break
    case 'acceptIncomingCall':
      callService.acceptIncomingCallByRoomId(payload.roomId)
      break
    case 'rejectIncomingCall': // different with reqRejectIncomingCall
      callService.rejectIncomingCallByRoomId(payload.roomId)
      break
    case 'newShareTarget':
      handleNewShareTarget(payload)
      break

    default:
      break
  }
}

export const isContactPermissionAllowedByClientUser = () => {
  return (
    sessionStorageGetItem(SessionStorageKeys.CONTACT_PERM) ===
    ContactPermission.allow
  )
}

export const sendFileToTwa = (file: File) => {
  console.log('sendFileToTwa ', file)
  const authToken = LocalFind('auth')?.token

  const form = new FormData()
  form.append('file', file)
  return axios.post(`http://localhost:${twaRunFilePort}/run-file`, form, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  })
}
